import React from 'react';
import { fmtCompact, fmtUsd, fmtPct } from '../../../utils/currencyDashboardUtils';
import type { KPIResult } from '../../../utils/currencyDashboardUtils';

interface KpiStripProps {
  kpis: KPIResult;
}

interface TileProps {
  accentColor: string;
  label: string;
  value: string;
  sub?: React.ReactNode;
}

const Tile: React.FC<TileProps> = ({ accentColor, label, value, sub }) => (
  <div className="flex items-stretch flex-1 min-w-0">
    <div className={`w-0.5 flex-none ${accentColor}`} />
    <div className="px-4 py-3 flex-1 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1">{label}</p>
      <p className="text-[15px] font-bold text-[#0D0D0D] tabular-nums leading-tight">{value}</p>
      {sub && <div className="mt-0.5">{sub}</div>}
    </div>
  </div>
);

export const KpiStrip: React.FC<KpiStripProps> = ({ kpis }) => {
  const momPositive = (kpis.momPct ?? 0) >= 0;
  const yoyPositive = (kpis.yoyPct ?? 0) >= 0;

  return (
    <div className="space-y-2">
      {/* Total Revenue card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
        <div className="flex items-stretch">
          <div className="w-0.5 flex-none bg-blue-500" />
          <div className="px-4 py-3 flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1">Total Revenue</p>
            <p className="text-[15px] font-bold text-[#0D0D0D] tabular-nums leading-tight">{fmtCompact(kpis.totalUsd)}</p>
            <div className="mt-2 flex items-center gap-2">
              {/* MoM indicator */}
              <span className={`text-[11px] font-semibold tabular-nums ${momPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                {momPositive ? '↑' : '↓'} MoM {fmtPct(kpis.momPct)}
              </span>
              <span className="text-slate-200">·</span>
              {/* YoY indicator */}
              <span className={`text-[11px] font-semibold tabular-nums ${yoyPositive ? 'text-violet-600' : 'text-orange-600'}`}>
                {yoyPositive ? '↑' : '↓'} YoY {fmtPct(kpis.yoyPct)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Avg / Month card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
        <div className="flex items-stretch">
          <div className="w-0.5 flex-none bg-slate-400" />
          <div className="px-4 py-3 flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1">Avg / Month</p>
            <p className="text-[15px] font-bold text-[#0D0D0D] tabular-nums leading-tight">{fmtCompact(kpis.avgPerMonthUsd)}</p>
            <div className="mt-2 text-[11px] text-slate-600">
              <span>3M avg: <span className="font-semibold text-slate-600">{fmtCompact(kpis.threeMonthAvg)}</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata row */}
      <p className="text-[11px] text-slate-600 px-1">
        {Math.round(kpis.invoiceCount).toLocaleString('en-US')} invoices
        {' · '}
        {kpis.uniqueClients} clients
        {' · '}
        {kpis.currenciesUsed} currencies
      </p>
    </div>
  );
};

export default KpiStrip;
